import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import ProgressLoader from "../../ProgressLoader";
import CircularProgress from "../../CircularProgress";
import IndexedDb from "../../../../../Helpers/IndexedDb";
import Translate from "./Translate";
import { captionLink, generateImageThumbnail, getDbInstanceName, getThumbBase64URL, isBlobUrl } from "../../../../../Helpers/Utility";
import { getLocalUserDetails } from "../../../../../Helpers/Chat/User";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import "./ImageComponent.scss";
import { RetryIcon, DownloadFileIcon } from "../../../../../assets/images";
import { useMessageProcessing } from "./useMessageProcessing";
const HtmlToReactParser = require('html-to-react').Parser;
const htmlToReactParser = new HtmlToReactParser();

const ImageComponent = (props = {}) => {
  const {
    imgSrc,
    handleMediaShow,
    isSender = true,
    uploadStatus = 0,
    messageObject = {},
    imgFileDownloadOnclick,
    imageHeightAdjust = false,
    pageType,
    imageUrlDownloaded,
    fileKey,
    isImageDownloaded = false,
    isUploadingCancelled = false,
  } = props;
  const {
    msgId = "",
    msgBody: {
      translatedMessage = "",
      message_type = "",
      media: { caption = "", file_url = "", thumb_image = "", webWidth, webHeight, file_key, file, file_size } = {},
      mentionedUsersIds = []
    } = {}
  } = messageObject;
  const localDb = useMemo(() => new IndexedDb(), []);
  const mediaImageThumbData = useSelector((state) => state.mediaImageThumbData);
  // Always start with thumbnail, only show blob URL when downloaded
  const [imageSource, setImageSource] = useState(getThumbBase64URL(thumb_image));
  const [imageThumbUrl, setImageThumbUrl] = useState(mediaImageThumbData[file?.fileDetails?.fileId]?.thumbImage)
  const [downloadStatus, setDownloadStatus] = useState(''); // '', 'downloading', 'cancelled', 'failed'
  const [isDownloading, setIsDownloading] = useState(false);
  const vcardData = getLocalUserDetails();
  const imageUrlCallbackCalledRef = useRef(false);

  const { messageRef } = useMessageProcessing({
    messageObject,
    isSender,
    vCardData: { data: vcardData || {} },
    htmlToReactParser
  });

  const generateThumbImage = useCallback(async (file) => {
    if (!file) return;
    const fileName = file?.name;
    const fileExtension = fileName?.split('.').pop();
    try {
      const thumbImage = await generateImageThumbnail(file, fileExtension);
      setImageThumbUrl(thumbImage);
    } catch (error) {
      console.error("Error generating thumbnail:", error);
    }
  }, []);

  useEffect(() => {
    if (imgSrc) {
      const isBlobImage = imgSrc.search("blob:") !== -1;
      const isDataUrl = imgSrc.search("data:") !== -1;
      // Only set blob URLs or data URLs, otherwise keep thumbnail
      if (isBlobImage || isDataUrl) {
        setImageSource(imgSrc);
        // Call imageUrlDownloaded callback only for blob URLs once
        if (imageUrlDownloaded && !imageUrlCallbackCalledRef.current) {
          imageUrlDownloaded(imgSrc);
          imageUrlCallbackCalledRef.current = true;
        }
      } else {
        // Non-blob URL, show thumbnail instead
        setImageSource(getThumbBase64URL(thumb_image));
      }
    } else if(thumb_image) {
      // Always prefer thumbnail first if no blob URL is provided
      setImageSource(getThumbBase64URL(thumb_image));
    } else if (file) {
      generateThumbImage(file);
    }
  }, [imgSrc, thumb_image, file, generateThumbImage, imageUrlDownloaded]);

  useEffect(() => {
    // Don't auto-download - only update if it's already a blob URL
    if (message_type === "image" && file_url && isBlobUrl(file_url)) {
      setImageSource(file_url);
      // Call imageUrlDownloaded callback for blob URLs once
      if (imageUrlDownloaded && !imageUrlCallbackCalledRef.current) {
        imageUrlDownloaded(file_url);
        imageUrlCallbackCalledRef.current = true;
      }
    }
  }, [file_url, message_type]);

  const isTranslated = () =>
    isSender && pageType === "conversation" && translatedMessage && Object.keys(translatedMessage).length;

  // Determine whether the image can be opened in the viewer.
  // Allow opening when the source is a blob URL, a data (base64) URL, or the image is marked as downloaded.
  const canOpen = Boolean(
    imageSource && (
      imageSource.startsWith("blob:") ||
      imageSource.startsWith("data:") ||
      isImageDownloaded
    )
  );

  // Format file size
  const formatSize = (bytes) => {
    if (!bytes) return "256 KB"; // Default estimate
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle download click
  const handleDownloadClick = async (e) => {
    e.stopPropagation();
    setIsDownloading(true);
    setDownloadStatus('downloading');
    
    if (imgFileDownloadOnclick) {
      imgFileDownloadOnclick({ target: { id: file_url } }, () => {
        // Reset after download completes
        setTimeout(() => {
          setIsDownloading(false);
          setDownloadStatus(''); // Clear status after successful download
        }, 1000);
      });
    }
  };

  // Handle cancel download
  const handleCancelDownload = (e) => {
    e.stopPropagation();
    setIsDownloading(false);
    setDownloadStatus('cancelled');
  };

  // Handle retry download
  const handleRetryDownload = (e) => {
    e.stopPropagation();
    setDownloadStatus('');
    handleDownloadClick(e);
  };

  return (
    <>
      <div className="image-wrapper">
      <div
          className={`image-message`}
          role="button"
          tabIndex={(handleMediaShow && canOpen) ? 0 : -1}
          onClick={(e) => {
            // Open viewer when the image source is openable (blob/data/or already downloaded)
            if (handleMediaShow && canOpen) {
              handleMediaShow(e);
            } else if (!isImageDownloaded && uploadStatus === 3 && imgFileDownloadOnclick) {
              // Trigger download when clicking on non-downloaded image
              imgFileDownloadOnclick({ target: { id: file_url } });
            }
          }}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && handleMediaShow && canOpen) {
              e.preventDefault();
              handleMediaShow(e);
            } else if ((e.key === 'Enter' || e.key === ' ') && !isImageDownloaded && uploadStatus === 3 && imgFileDownloadOnclick) {
              e.preventDefault();
              imgFileDownloadOnclick({ target: { id: file_url } });
            }
          }}
        >
          <img 
            className="webchat-conver-image" 
            style={imageSource.search("blob:") !== -1 ? {cursor:"pointer"} : {cursor:"auto"}} 
            src={
              // Show blob or data URL if available (for both incoming and outgoing)
              (imageSource.search("blob:") !== -1 || imageSource.search("data:") !== -1) ? imageSource :
              // For current user's messages during upload (uploadStatus 0 or 1), check if imgSrc is blob/data URL
              (!isSender && (uploadStatus === 0 || uploadStatus === 1) && imgSrc && (imgSrc.search("blob:") !== -1 || imgSrc.search("data:") !== -1)) ? imgSrc :
              // For outgoing messages during upload, show thumbnail from generated thumb (only if no blob available)
              (!isSender && (uploadStatus === 0 || uploadStatus === 1) && imageThumbUrl) ? getThumbBase64URL(imageThumbUrl) :
              // Show thumbnail when not downloaded (both incoming and outgoing)
              (uploadStatus === 2 && !isImageDownloaded && imageThumbUrl && imageSource.search("blob:") === -1 && imageSource.search("data:") === -1) ? getThumbBase64URL(imageThumbUrl) :
              (uploadStatus === 3 && (thumb_image || imageThumbUrl)) ? getThumbBase64URL(imageThumbUrl || thumb_image) :
              // Default to imageSource
              imageSource
            } 
            alt="chat-img" 
          />
          
          {/* Download overlay states - for both incoming and outgoing messages that need download */}
          {!isImageDownloaded && ((uploadStatus === 2 && isSender) || uploadStatus === 3) && (
            <div className="common-download-overlay">
              {isDownloading ? (
                // Show cancel button and progress during download
                <div className="download-progress-container">
                  <div className="download-progress-info">
                    <div className="download-progress-wrapper">
                      <CircularProgress 
                        progress={50}
                        size={56}
                        strokeWidth={4}
                      />
                    </div>
                    <button 
                      className="cancel-button"
                      onClick={handleCancelDownload}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleCancelDownload(e);
                        }
                      }}
                      aria-label="Cancel download"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M6 6L18 18M6 18L18 6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ) : downloadStatus === 'cancelled' || downloadStatus === 'failed' ? (
                // Show retry button
                <div 
                  className="retry-download-button"
                  onClick={handleRetryDownload}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleRetryDownload(e);
                    }
                  }}
                >
                  <RetryIcon/>
                </div>
              ) : (
                // Show initial download button with size
                <div 
                  className="initial-download-button"
                  onClick={handleDownloadClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleDownloadClick(e);
                    }
                  }}
                >
                  <div className="download-info-wrapper">
                    <DownloadFileIcon style={{ width: "16px", height: "16px" }}/>
                    <div className="download-size-details">
                      <span className="size-large">{formatSize(file_size)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Loading spinner during download (uploadStatus 4) */}
          {uploadStatus === 4 && (
            <div className="single-media-download-overlay" style={{ zIndex: 3 }}>
              <div className="single-spinner-container">
                <div className="single-spinner"></div>
              </div>
            </div>
          )}
        </div>
        <ProgressLoader
          msgId={msgId}
          isSender={isSender}
          file_url={file_url}
          uploadStatus={uploadStatus}
          imgFileDownloadOnclick={imgFileDownloadOnclick}
          messageType = {"image"}
          downloadEnabled ={imageSource.search("blob:") !== -1}
          isUploadingCancelled={isUploadingCancelled}
        />
      </div>
      <div className="image-caption" ref={messageRef}>
        {caption !== "" && (
          <span>
            <span>{captionLink(caption,mentionedUsersIds,vcardData.userId, messageObject.chatType)}</span>
            {!!isTranslated() && <Translate tMessage={translatedMessage} />}
          </span>
        )}
      </div>
    </>
  );
};

ImageComponent.propTypes = {
  imgSrc: PropTypes.string,
  handleMediaShow: PropTypes.func,
  isSender: PropTypes.bool,
  uploadStatus: PropTypes.number,
  messageObject: PropTypes.object,
  imgFileDownloadOnclick: PropTypes.func,
  imageHeightAdjust: PropTypes.bool,
  pageType: PropTypes.string,
  imageUrlDownloaded: PropTypes.func,
  isImageDownloaded: PropTypes.bool,
  isUploadingCancelled: PropTypes.bool,
};

export default React.memo(ImageComponent);